import { inject, Injectable, OnDestroy } from '@angular/core';
import { TerminalService } from '../terminal.service';
import { BalanceService } from './balance.service';
import { TradeKey } from 'src/app/core/model/api';
import { BehaviorSubject, EMPTY, filter, map, merge, Observable, of, shareReplay, Subscription, switchMap, tap } from 'rxjs';
import { KeysService } from '../keys/keys.service';
import { TradeKeyWithPermissions } from 'src/app/core/model/common';

@Injectable({
  providedIn: 'root'
})
export class BalanceFlowService implements OnDestroy {

  private terminalService = inject(TerminalService)
  private balanceService = inject(BalanceService)
  private keysService = inject(KeysService)

  private readySubject = new BehaviorSubject<boolean>(false)
  private sb: Subscription
  private flows$ = new Map<number, Observable<number>>()
  private coinFlows$ = new Map<number, Observable<Record<string, number>>>()

  constructor() {
    this.sb = this.keysService.keys$.subscribe(keys => {
      const keysIds = keys.map(k => k.id)
      const existsIds = Array.from(this.flows$.keys())
      const newIds = keysIds.filter(id => !existsIds.includes(id))
      const deleteIds = existsIds.filter(id => !keysIds.includes(id))

      deleteIds.forEach(id => {
        this.flows$.delete(id)
        this.coinFlows$.delete(id)
      })
      newIds.forEach(id => {
        const key = keys.find(k => k.id == id)
        this.flows$.set(id, key ? this.createBalanceFlow(key) : EMPTY)
        this.coinFlows$.set(id, key ? this.createCoinBalancesFlow(key) : EMPTY)
      })
      this.readySubject.next(true)
    })
  }

  ngOnDestroy(): void {
    this.readySubject.complete()
    this.sb.unsubscribe()
  }

  private createBalanceFlow(key: TradeKeyWithPermissions) {
    if (key.isDemo || !key.canRead) {
      return of(0)
    }

    const rest = this.terminalService.getRestClientForKey(key)
    const ws = this.terminalService.getWebsocketClientForKey(key)

    return merge(
      this.balanceService.requestBalance(rest),
      this.balanceService.realtimeBalance(rest, ws)
    ).pipe(
      shareReplay(1)
    )
  }

  private createCoinBalancesFlow(key: TradeKeyWithPermissions) {
    if (key.isDemo || !key.canRead) {
      return of({} as Record<string, number>)
    }

    const rest = this.terminalService.getRestClientForKey(key)
    const ws = this.terminalService.getWebsocketClientForKey(key)

    let lastBalances: Record<string, number> = {}
    return merge(
      this.balanceService.requestBalances(rest).pipe(
        tap(data => lastBalances = data)
      ),
      this.balanceService.realtimeBalances(rest, ws).pipe(
        map(data => lastBalances = Object.assign(lastBalances, data)),
      )
    ).pipe(
      shareReplay(1)
    )
  }

  getBalanceFlow(key: TradeKey) {
    return this.readySubject.pipe(
      filter(flag => flag),
      switchMap(() => this.flows$.get(key.id) ?? EMPTY)
    )
  }

  getCoinBalancesFlow(key: TradeKey) {
    return this.readySubject.pipe(
      filter(flag => flag),
      switchMap(() => this.coinFlows$.get(key.id) ?? EMPTY)
    )
  }

}
